/**
 * Redux slice (src/store/slices/dashboardSlice.js).
 *
 * Data flow (typical):
 * - UI dispatches actions/thunks from `src/store/actions/*`.
 * - `reducers` handles synchronous state updates.
 * - `extraReducers` reacts to thunk pending/fulfilled/rejected actions.
 */

// src/store/slices/dashboardSlice.js
import { createSlice } from "@reduxjs/toolkit";
import { getAdminDashboardStats } from "../actions/dashboardActions";

/**
 * Slice initial state.
 *
 * Keep this shape stable because it is consumed across many selectors/components.
 */
const initialState = {
  loading: false,
  error: null,
  errorStatus: null,
  errorCode: null,
  lastUpdated: null,
  timeRange: 30,
  
  // Overview data
  overview: {
    totalUsers: 0,
    totalStudents: 0,
    totalTeachers: 0,
    totalAdmins: 0,
    totalBookings: 0,
    completedBookings: 0,
    totalRevenue: 0,
    activePackages: 0,
    activeCourses: 0,
    timeRange: "30 days"
  },
  
  // Growth metrics
  growth: {
    newStudents: 0,
    newTeachers: 0,
    studentGrowth: 0,
    teacherGrowth: 0,
    bookingGrowth: 0,
    revenueGrowth: 0
  },
  
  // Booking statistics
  bookings: {
    total: 0,
    completed: 0,
    scheduled: 0,
    cancelled: 0,
    rescheduled: 0,
    completionRate: 0,
    recent: []
  },
  
  // Financial data
  financial: {
    totalRevenue: 0,
    recentRevenue: 0,
    averageOrderValue: 0,
    totalTransactions: 0,
    revenueByMonth: []
  },
  
  // Teacher statistics
  teachers: {
    total: 0,
    active: 0,
    withBookings: 0,
    averageRating: 0,
    topPerformers: []
  },
  
  // Package statistics
  packages: {
    total: 0,
    mostPopular: [],
    salesData: []
  },
  
  // Course statistics
  courses: {
    total: 0,
    withPackages: 0,
    averagePackagesPerCourse: 0
  },
  
  // Enquiry statistics
  enquiries: {
    total: 0,
    recent: 0,
    pending: 0
  },

  notifications: {
    total: 0,
    byType: {},
    recent: 0,
  },
  
  // Activity data
  activity: {
    recent: [],
    monthlyGrowth: []
  },

  userEngagement: {
    activeUsers: 0,
    averageSessionsPerUser: 0,
    retentionRate: 0,
    atRiskUsers: 0,
    geographicDistribution: [],
    timezoneDistribution: [],
  },

  conversions: {
    enquiryToBooking: 0,
    trialToPackage: 0,
    signupToFirstBooking: 0,
    acquisitionChannels: [],
  },

  systemHealth: {
    uptime: 0,
    averageResponseTime: 0,
    errorRate: 0,
    totalRequests: 0,
  },

  operations: {
    enquiries: {
      total: 0,
      recent: 0,
      pending: 0,
    },
    notifications: {
      total: 0,
      byType: {},
      recent: 0,
    },
    recentActivity: [],
    monthlyGrowth: [],
  },

  accounting: {
    scope: "accounting",
    cutoffStart: null,
    cutoffActive: false,
    legacyExcluded: true,
    archiveOnly: false,
  },
};

/**
 * RTK slice definition: dashboardSlice.
 */
const dashboardSlice = createSlice({
  name: "dashboard",
  initialState,
  reducers: {
    /**
     * Reducer: clearDashboardErrors.
     */
    clearDashboardErrors: (state) => {
      state.error = null;
      state.errorStatus = null;
      state.errorCode = null;
    },
    
    /**
     * Reducer: setTimeRange.
     */
    setTimeRange: (state, action) => {
      state.timeRange = action.payload;
    },
    
    /**
     * Reducer: clearDashboardData.
     */
    clearDashboardData: (state) => {
      // Reset to initial state except loading and error
      Object.assign(state, {
        ...initialState,
        loading: state.loading,
        error: state.error
      });
    }
  },
  
  /**
   * Reducer: extraReducers.
   */
  extraReducers: (builder) => {
    builder
      // extraReducers handler: getAdminDashboardStats.pending
      .addCase(getAdminDashboardStats.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.errorStatus = null;
        state.errorCode = null;
      })
      // extraReducers handler: getAdminDashboardStats.fulfilled
      .addCase(getAdminDashboardStats.fulfilled, (state, action) => {
        state.loading = false;
        
        if (
          action.payload?.success &&
          action.payload?.data &&
          typeof action.payload.data === "object" &&
          action.payload.data.overview &&
          typeof action.payload.data.overview === "object"
        ) {
          state.error = null;
          state.errorStatus = null;
          state.errorCode = null;
          state.lastUpdated = new Date().toISOString();
          const data = action.payload.data;
          const operations = data.operations || {};
          const mappedActivity = data.activity || {
            recent: operations.recentActivity || [],
            monthlyGrowth: operations.monthlyGrowth || [],
          };
          const mappedEnquiries = data.enquiries || operations.enquiries || {};
          const mappedNotifications =
            data.notifications || operations.notifications || {};
          
          // Update all dashboard sections with the received data
          state.overview = { ...state.overview, ...data.overview };
          state.growth = { ...state.growth, ...data.growth };
          state.bookings = { ...state.bookings, ...data.bookings };
          state.financial = { ...state.financial, ...data.financial };
          state.teachers = { ...state.teachers, ...data.teachers };
          state.packages = { ...state.packages, ...data.packages };
          state.courses = { ...state.courses, ...data.courses };
          state.enquiries = { ...state.enquiries, ...mappedEnquiries };
          state.notifications = {
            ...state.notifications,
            ...mappedNotifications,
          };
          state.activity = { ...state.activity, ...mappedActivity };
          state.userEngagement = {
            ...state.userEngagement,
            ...data.userEngagement,
          };
          state.conversions = { ...state.conversions, ...data.conversions };
          state.systemHealth = { ...state.systemHealth, ...data.systemHealth };
          state.operations = { ...state.operations, ...operations };
          state.accounting = {
            ...state.accounting,
            ...data.accounting,
          };
          
          // Update time range from the response
          state.timeRange = data.overview.timeRange ? 
            parseInt(data.overview.timeRange.split(' ')[0]) : 30;
          return;
        }

        // A fulfilled action can still be dispatched by a legacy caller. It
        // must not make the initial zero-shaped state look confirmed.
        state.error = "Nie udało się załadować dashboardu.";
        state.errorStatus = null;
        state.errorCode = "ADMIN_DASHBOARD_INVALID_RESPONSE";
      })
      // extraReducers handler: getAdminDashboardStats.rejected
      .addCase(getAdminDashboardStats.rejected, (state, action) => {
        state.loading = false;
        state.error = "Nie udało się załadować dashboardu.";
        state.errorStatus = Number(action.payload?.status) || null;
        state.errorCode = action.payload?.code || "ADMIN_DASHBOARD_UNAVAILABLE";
      });
  },
});

export const { 
  clearDashboardErrors, 
  setTimeRange, 
  clearDashboardData 
} = dashboardSlice.actions;

export default dashboardSlice.reducer;
